// Outdoor: the high-contrast look for reading the iPad in daylight (#707,
// docs/mockups/outdoor-mode.html). Off until the user turns it on; remembered
// on the device, not the account, because devices are not shared. While it is
// on, <html> carries data-outdoor and src/index.css redefines the colours,
// line weights and sizes the screens already read.
import { useLayoutEffect } from 'react';
import { atom, useAtom } from 'jotai';
import { atomWithStorage, createJSONStorage } from 'jotai/utils';

const OUTDOOR_KEY = 'm2q.outdoor';
const SUN_TIPS_SEEN_KEY = 'm2q.outdoor.tipsSeen';
const OUTDOOR_ATTRIBUTE = 'data-outdoor';

type StringStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

/** Device storage that never throws: blocked or private storage reads as unset and forgets writes. */
export function safeStorage(open: () => StringStorage): StringStorage {
  return {
    getItem(key) {
      try {
        return open().getItem(key);
      } catch {
        return null;
      }
    },
    setItem(key, value) {
      try {
        open().setItem(key, value);
      } catch {
        // Storage blocked: Outdoor still switches, it just isn't remembered.
      }
    },
    removeItem(key) {
      try {
        open().removeItem(key);
      } catch {
        // As above.
      }
    },
  };
}

const deviceStorage = createJSONStorage<boolean>(() => safeStorage(() => localStorage));

/** A yes/no remembered on this device, read at start-up so the first paint is already right. */
function deviceFlagAtom(key: string) {
  return atomWithStorage<boolean>(key, false, deviceStorage, { getOnInit: true });
}

const outdoorAtom = deviceFlagAtom(OUTDOOR_KEY);
const sunTipsSeenAtom = deviceFlagAtom(SUN_TIPS_SEEN_KEY);
const sunTipsOpenAtom = atom(false);

/** The "Working in bright sun" card opens by itself only the first time Outdoor is turned on here. */
export function opensSunTips(turningOn: boolean, seen: unknown): boolean {
  return turningOn && seen !== true;
}

/** Whether Outdoor is on here, and the switch. Anything stored that isn't `true` counts as off. */
export function useOutdoor(): [boolean, (on: boolean) => void] {
  const [stored, setStored] = useAtom(outdoorAtom);
  const [seen, setSeen] = useAtom(sunTipsSeenAtom);
  const [, setTipsOpen] = useAtom(sunTipsOpenAtom);
  const setOutdoor = (on: boolean) => {
    setStored(on);
    if (opensSunTips(on, seen)) {
      setSeen(true);
      setTipsOpen(true);
    }
  };
  return [stored === true, setOutdoor];
}

/** Whether the "Working in bright sun" card is showing, and how to open or close it. */
export function useSunTips(): [boolean, (open: boolean) => void] {
  return useAtom(sunTipsOpenAtom);
}

/** Keeps <html data-outdoor> in step with the setting, so dialogs and toasts outside the layout follow it too. */
export function useOutdoorPage(): void {
  const [on] = useOutdoor();
  useLayoutEffect(() => {
    document.documentElement.toggleAttribute(OUTDOOR_ATTRIBUTE, on);
  }, [on]);
}
